import { useEffect, useMemo, useState } from 'react'
import { RenderIf } from 'components/RenderIf'

import { convertToPrice } from 'featureSliced/shared/utils/convertToPrice'
import { Button } from 'featureSliced/entities/Button'
import { Spinner } from 'featureSliced/entities/Spinner'
import { QRMBucketSlice } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer'
import {
  findTemplateUnavailableLines,
  flattenTopLevelProducts,
} from 'featureSliced/shared/utils/templateDays'

import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'

import { TableStatuses } from 'featureSliced/shared/store/reducers/QRMenuTableReducer/types'
import { BucketProducts } from './BucketProducts'

import { Header } from 'components/Header'
import { qrMenuPOST } from 'featureSliced/shared/api/qrMenu/POST'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useOpenModal } from 'featureSliced/shared/hooks/useOpenModal'
import { Success } from 'sections/Order/OrderModal/successModal'
import { ConfirmationModal } from 'featureSliced/features/ConfirmationModal'
import { AgeProtectionModal } from 'featureSliced/widgets/AgeProtectionModal'
import { useProductProtection } from 'featureSliced/shared/hooks/useProductProtection'
import { getNavLink } from 'featureSliced/shared/utils/getNavLink'
import { useWebLayout } from 'featureSliced/shared/hooks/useWebLayout'
import { CheckoutWeb } from 'featureSliced/widgets/WebLayout/CheckoutWeb'

import {
  BucketButtonContainer,
  BucketContainer,
  BucketFooter,
  Total,
} from './styled'
import { getProductDiscount } from 'featureSliced/shared/utils/getProductDiscount'
import { getDiscountedPrice } from 'featureSliced/shared/utils/getDiscountedPrice'
import {
  getPrecision,
  roundValueTo,
} from 'featureSliced/shared/utils/roundValueTo'
import { ToastsSlice } from 'featureSliced/shared/store/reducers/ToasterReducer'
import { useGetProductPrice } from 'featureSliced/shared/hooks/useGetProductPrice/indexT'
import { useSoldOutRefusal } from 'featureSliced/shared/hooks/useSoldOutRefusal'
import { getSoldOutItems } from 'featureSliced/shared/store/reducers/CategoriesReducer/actionCreator'
import { getProductNote } from 'featureSliced/shared/utils/getProductNote'

export const Bucket = () => {
  // Hooks
  const t = useTranslations()
  const { isWeb } = useWebLayout()
  const dispatch = useMyDispatch()
  const openModal = useOpenModal()
  const [searchParams] = useSearchParams()
  const { getAllProductsPrice, applyOfferPricesToBucket } = useGetProductPrice()
  const { isProductProtected, isProductHighlyProtected } =
    useProductProtection()
  const navigate = useNavigate()
  const handleSoldOutRefusal = useSoldOutRefusal()

  // Store
  const tableStatus = useMySelector((state) => state.qrMenuTable.tableStatus)
  const Currency = useMySelector((state) => state.app.companyData.Currency)
  const orderGuid = useMySelector((state) => state.qrMenuTable.orderGuid)
  const currency = useMySelector((state) => state.app.companyData.Currency)
  const tableData = useMySelector((state) => state.common.tableData)
  const selectedQRMProducts = useMySelector((state) => state.bucket.products)
  const topLevelProducts = useMySelector((state) =>
    flattenTopLevelProducts(state.categories.items)
  )
  const appTheme = useMySelector((state) => state.app.interfaceStyles.appTheme)
  const discounts = useMySelector((state) => state.specials?.discounts)
  const offerPrices = useMySelector((state) => state.specials?.offerPrices)
  const { isTableLoading, isTableDataLoaded } = useMySelector(
    (state) => state.qrMenuTable
  )
  const disabledByMobileWaiter = useMySelector(
    (state) => state.app.menu.disabledByMobileWaiter
  )

  // State
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const table = searchParams.get('table')
  const openToast = ToastsSlice.actions.openToast
  const clearBucket = QRMBucketSlice.actions.clearBucket
  const removeProductFromBucket = QRMBucketSlice.actions.removeProductFromBucket
  const tableName = tableData.caption || tableData.objectTag

  // useMemo
  const bucketProducts = useMemo(() => {
    return applyOfferPricesToBucket(selectedQRMProducts)?.map((el) => {
      const discountP = el.offerApplied ? 0 : getProductDiscount(el, discounts)
      return {
        ...el,
        discountP,
        discountType: discountP ? 50 : 0,
        total: roundValueTo(
          getDiscountedPrice((el.price ?? 0) * (el.count ?? 1), discountP),
          getPrecision(Currency)
        ),
      }
    })
  }, [selectedQRMProducts, offerPrices])

  // useEffect
  useEffect(() => {
    if (isTableDataLoaded && !isTableLoading) {
      if (
        tableStatus === TableStatuses.UNDEFINED ||
        tableStatus === TableStatuses.BUSY
      ) {
        navigate(getNavLink(searchParams, 'menu'))
      }
    }
  }, [tableStatus, isTableDataLoaded, isTableLoading])

  useEffect(() => {
    if (!bucketProducts?.length || process.env.REACT_APP_IS_CLIC !== '0') {
      navigate(getNavLink(searchParams, 'menu'))
    }
  }, [bucketProducts])

  // Modals
  const openSuccuessOrderModal = () => {
    openModal({
      id: 'SUCCESS_MODAL',
      components: [
        ({ closeThisModal }) => <Success closeModal={closeThisModal} />,
      ],
      onClose: () => {
        dispatch(clearBucket())
      },
    })
  }

  const openAgeProtectionModal = (isHighlyProtected = false, index = 0) => {
    openModal({
      id: 'AGE_PROTECTION_MODAL',
      title: t('common.confirmYourAge'),
      components: [
        ({ closeThisModal }) => (
          <AgeProtectionModal
            isHighlyProtected={isHighlyProtected}
            startPayment={() => {
              bookOrder(index)
              closeThisModal()
            }}
          />
        ),
      ],
    })
  }

  // Functions
  const createOrder = async (index = 0) => {
    if (disabledByMobileWaiter) {
      dispatch(
        openToast({
          // TODO: translations
          message: t('order.tableLockedByWaiter'),
          type: 'error',
        })
      )
    } else if (!isLoading) {
      const templateUnavailableLines = findTemplateUnavailableLines(
        bucketProducts,
        topLevelProducts
      )
      if (templateUnavailableLines.length) {
        templateUnavailableLines.forEach((product) => {
          dispatch(removeProductFromBucket(product.uniqueId))
        })
        dispatch(
          openToast({
            message: t('products.someProductsNotAvailable') || '',
            type: 'warning',
          })
        )
        navigate(getNavLink(searchParams, 'menu'))
        return
      }

      const soldOutIds = await dispatch(getSoldOutItems())
      const soldOutProducts = bucketProducts?.filter((product) =>
        soldOutIds?.includes(product.productId)
      )

      if (soldOutProducts?.length) {
        // Remove all sold out products from bucket
        soldOutProducts.forEach((product) => {
          if (product.uniqueId) {
            dispatch(removeProductFromBucket(product.uniqueId))
          }
        })

        dispatch(
          openToast({
            message: t('products.someProductsAreSoldOut') || '',
            type: 'warning',
          })
        )

        navigate(getNavLink(searchParams, 'menu'))
      } else if (
        bucketProducts?.some((product) =>
          isProductHighlyProtected(product.productId)
        )
      ) {
        openAgeProtectionModal(true, index)
      } else if (
        bucketProducts?.some((product) => isProductProtected(product.productId))
      ) {
        openAgeProtectionModal(false, index)
      } else {
        await bookOrder(index)
      }
    }
  }

  const bookOrder = async (index = 0) => {
    if (!isLoading) {
      try {
        setIsLoading(true)
        const bucketProductsWithNotes = bucketProducts?.map((el) => ({
          ...el,
          freeText: getProductNote(el.productId || 0, el.categoryId || 0),
        }))
        const res = await qrMenuPOST.updateCloudOrders(
          table,
          bucketProductsWithNotes,
          orderGuid
        )
        if (tableStatus === TableStatuses.FREE && res && table) {
          await qrMenuPOST.bookProducts(
            table,
            bucketProductsWithNotes,
            res?.orderGUID
          )
        }
        navigate(getNavLink(searchParams, ''))
        openSuccuessOrderModal()
        dispatch(clearBucket())
      } catch (e: any) {
        if (handleSoldOutRefusal(e)) return
        // A template can close for the day between "added to cart" and "book" — the
        // server backstop caught what the pre-check above missed (QRM-648).
        if (e.code === 'TEMPLATE_NOT_AVAILABLE') {
          dispatch(
            openToast({
              message: t('products.someProductsNotAvailable') || '',
              type: 'error',
            })
          )
          navigate(getNavLink(searchParams, 'menu'))
        } else if (e.code === 1006) {
          navigate(getNavLink(searchParams, ''))
        } else {
          let failedOrderJSON: any
          try {
            failedOrderJSON = await import(
              `assets/lotties/${appTheme}/callWaiter.json`
            )
          } catch (e) {
            failedOrderJSON = await import(
              'assets/lotties/light/failedOrder.json'
            )
          }

          openModal({
            id: `ERROR_MODAL_${index}`,
            components: [
              ({ closeThisModal }) => (
                <ConfirmationModal
                  onConfirm={() => {
                    createOrder(index + 1)
                    closeThisModal()
                  }}
                  onCancel={() => {
                    navigate(getNavLink(searchParams, 'menu'))
                    closeThisModal()
                  }}
                  okText={t('buttons.tryAgain')}
                  animationJSON={failedOrderJSON}
                  text={t('errors.confirmFail')}
                />
              ),
            ],
            title: t('errors.commonErrorMessage'),
          })
        }
      } finally {
        setIsLoading(false)
      }
    }
  }

  // Same children in both shells, so the phone DOM stays exactly as it was and the
  // book button keeps its handler untouched (QRM-641).
  const content = (
    <>
      <BucketProducts />

      <BucketFooter>
        <Total>
          <span>Total:</span>
          <span>
            {convertToPrice(getAllProductsPrice(bucketProducts))}{' '}
            {currency || ''}
          </span>
        </Total>
        <BucketButtonContainer>
          <Button
            buttonType="common"
            width="50%"
            disabled={tableStatus === TableStatuses.BUSY || isLoading}
            onClick={createOrder}
          >
            {tableName
              ? t('order.bookTable', { tableNumber: tableName })
              : t('order.book')}
          </Button>
        </BucketButtonContainer>
      </BucketFooter>
    </>
  )

  if (isWeb) {
    return (
      <>
        <RenderIf condition={isLoading}>
          <Spinner type="transparent" />
        </RenderIf>
        {/* No summary column: this page IS the order review. No shell title either —
            BucketProducts already prints "Your order" as the list heading. */}
        <CheckoutWeb withSummary={false}>{content}</CheckoutWeb>
      </>
    )
  }

  return (
    <>
      <RenderIf condition={isLoading}>
        <Spinner type="transparent" />
      </RenderIf>
      <Header />
      <BucketContainer>{content}</BucketContainer>
    </>
  )
}
